import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useTheme } from 'hooks/useTheme'; 

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-7xl',
  full: 'max-w-full',
};

export const Drawer = ({
  title,
  children,
  isOpen,
  onClose,
  renderFooter,
  size = 'md',
}) => {
  const { theme, background } = useTheme(); // 🎯 useTheme hook

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 overflow-hidden z-50"
        open={isOpen}
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          {/* Overlay */}
          <Transition.Child
            as={React.Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          {/* Right drawer */}
          <div className="fixed inset-y-0 right-0 max-w-full flex">
            <Transition.Child
              as={React.Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className={clsx('w-screen', sizes[size])}>
                <div
                  className={clsx(
                    'h-full flex flex-col backdrop-blur-lg shadow-xl rounded-l-xl overflow-hidden relative text-white',
                    theme // 🎯 apply dynamic theme gradient class
                  )}
                >
                  {/* Optional background image */}
                  {background && (
                    <div className="absolute inset-0 pointer-events-none opacity-10 z-0">
                      <img
                        src={background}
                        alt="Decorative background"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="relative z-10 flex-1 flex flex-col overflow-y-auto">
                    {/* Header */}
                    <div className="px-4 py-4 sm:px-6 border-b border-white/20">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-lg font-medium">
                          {title}
                        </Dialog.Title>
                        <div className="ml-3 h-7 flex items-center">
                          <button
                            className="rounded-md p-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
                            onClick={onClose}
                          >
                            <span className="sr-only">Close panel</span>
                            <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="mt-6 relative flex-1 px-4 sm:px-6 text-white">
                      {children}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="relative z-10 flex-shrink-0 px-4 py-4 flex justify-end space-x-2 border-t border-white/20 bg-white/5">
                    {renderFooter()}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

Drawer.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  renderFooter: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
};
